import type { DefaultRootState } from 'react-redux';

import createReducer, { ActionCreatorHelper } from 'store/createReducer';

export enum BannerTextType {
    Default = 'DEFAULT',
    Generated = 'GENERATED',
    Edited = 'EDITED',
}

// ----------------------- actions -------------------------------------
const SET_BANNER_TEXT_WAS_GENERATED = 'SET_BANNER_TEXT_WAS_GENERATED';
const INCREMENT_BANNER_TEXT_GENERATION_COUNT = 'INCREMENT_BANNER_TEXT_GENERATION_COUNT';
const SET_BANNER_TEXT_GENERATION_IS_LOADING = 'SET_BANNER_TEXT_GENERATION_IS_LOADING';
const SET_BANNER_GENERATED_TEXT = 'SET_BANNER_GENERATED_TEXT';
const SET_BANNER_PREVIOUS_TEXT = 'SET_BANNER_PREVIOUS_TEXT';
const REMOVE_BANNER_GENERATED_TEXT = 'REMOVE_BANNER_GENERATED_TEXT';
const RESET_BANNER_TEXT_GENERATION = 'RESET_BANNER_TEXT_GENERATION';
const SET_BANNER_TEXT_TYPE = 'SET_BANNER_TEXT_TYPE';
const SET_BANNER_DEFAULT_TEXT = 'SET_BANNER_DEFAULT_TEXT';

interface PayloadTypes {
    [SET_BANNER_TEXT_WAS_GENERATED]: boolean;
    [INCREMENT_BANNER_TEXT_GENERATION_COUNT]: void;
    [SET_BANNER_TEXT_GENERATION_IS_LOADING]: boolean;
    [SET_BANNER_GENERATED_TEXT]: string;
    [SET_BANNER_PREVIOUS_TEXT]: string;
    [REMOVE_BANNER_GENERATED_TEXT]: void;
    [RESET_BANNER_TEXT_GENERATION]: void;
    [SET_BANNER_TEXT_TYPE]: BannerTextType;
    [SET_BANNER_DEFAULT_TEXT]: string;
}

const ActionCreator = ActionCreatorHelper<PayloadTypes>();

export const setBannerTextWasGenerated = ActionCreator(SET_BANNER_TEXT_WAS_GENERATED);
export const incrementBannerTextGenerationCount = ActionCreator(INCREMENT_BANNER_TEXT_GENERATION_COUNT);
export const setBannerTextGenerationIsLoading = ActionCreator(SET_BANNER_TEXT_GENERATION_IS_LOADING);
export const setBannerGeneratedText = ActionCreator(SET_BANNER_GENERATED_TEXT);
export const setBannerPreviousText = ActionCreator(SET_BANNER_PREVIOUS_TEXT);
export const removeBannerGeneratedText = ActionCreator(REMOVE_BANNER_GENERATED_TEXT);
export const resetBannerTextGeneration = ActionCreator(RESET_BANNER_TEXT_GENERATION);
export const setBannerTextType = ActionCreator(SET_BANNER_TEXT_TYPE);
export const setBannerDefaultText = ActionCreator(SET_BANNER_DEFAULT_TEXT);
// ----------------------- /actions -------------------------------------

// ----------------------- selectors ------------------------------------
export const selectBannerTextWasGenerated = (state: DefaultRootState): boolean =>
    state.bannerTextGeneration.bannerTextWasGenerated;
export const selectBannerTextGenerationCount = (state: DefaultRootState): number =>
    state.bannerTextGeneration.bannerTextGenerationCount;
export const selectBannerTextGenerationIsLoading = (state: DefaultRootState): boolean =>
    state.bannerTextGeneration.bannerTextGenerationIsLoading;
export const selectBannerGeneratedTexts = (state: DefaultRootState): string[] =>
    state.bannerTextGeneration.generatedTexts;
export const selectBannerPreviousText = (state: DefaultRootState): string =>
    state.bannerTextGeneration.bannerPreviousText;
export const selectBannerTextGeneration = (state: DefaultRootState): BannerTextGenerationState =>
    state.bannerTextGeneration;
export const selectBannerTextType = (state: DefaultRootState): BannerTextType =>
    state.bannerTextGeneration.bannerTextType;
export const selectBannerDefaultText = (state: DefaultRootState): string =>
    state.bannerTextGeneration.bannerDefaultText;
// ----------------------- /selectors ------------------------------------

// ----------------------- reducer --------------------------------------
export interface BannerTextGenerationState {
    bannerTextWasGenerated: boolean;
    bannerTextGenerationCount: number;
    bannerTextGenerationIsLoading: boolean;
    generatedTexts: string[];
    bannerPreviousText: string;
    bannerTextType: BannerTextType;
    bannerDefaultText: string;
}

export const BANNER_TEXT_GENERATION_INITIAL_STATE: BannerTextGenerationState = {
    bannerTextWasGenerated: false,
    bannerTextGenerationCount: 0,
    bannerTextGenerationIsLoading: false,
    generatedTexts: [],
    bannerPreviousText: '',
    bannerTextType: BannerTextType.Default,
    bannerDefaultText: '',
};

export default createReducer<BannerTextGenerationState, PayloadTypes>(BANNER_TEXT_GENERATION_INITIAL_STATE, {
    [SET_BANNER_TEXT_WAS_GENERATED]: (state, { payload }) => ({
        ...state,
        bannerTextWasGenerated: payload,
    }),
    [INCREMENT_BANNER_TEXT_GENERATION_COUNT]: (state) => ({
        ...state,
        bannerTextGenerationCount: state.bannerTextGenerationCount + 1,
    }),
    [SET_BANNER_TEXT_GENERATION_IS_LOADING]: (state, { payload }) => ({
        ...state,
        bannerTextGenerationIsLoading: payload,
    }),
    [SET_BANNER_GENERATED_TEXT]: (state, { payload }) => ({
        ...state,
        generatedTexts: [payload, ...state.generatedTexts],
    }),
    [SET_BANNER_PREVIOUS_TEXT]: (state, { payload }) => ({
        ...state,
        bannerPreviousText: payload,
    }),
    [REMOVE_BANNER_GENERATED_TEXT]: (state) => ({
        ...state,
        generatedTexts: state.generatedTexts.slice(1),
    }),
    [RESET_BANNER_TEXT_GENERATION]: () => ({
        ...BANNER_TEXT_GENERATION_INITIAL_STATE,
    }),
    [SET_BANNER_TEXT_TYPE]: (state, { payload }) => ({
        ...state,
        bannerTextType: payload,
    }),
    [SET_BANNER_DEFAULT_TEXT]: (state, { payload }) => ({
        ...state,
        bannerDefaultText: payload,
    }),
});
// ----------------------- /reducer --------------------------------------
